import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'
import {
	ToggleButton as AriaToggleButton,
	ToggleButtonGroup as AriaToggleButtonGroup,
	type ToggleButtonGroupProps as AriaToggleButtonGroupProps,
	type ToggleButtonProps as AriaToggleButtonProps,
} from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'

/**
 * The Pills Tabs set on the Creative canvas (193:5229): Selected = True / False.
 * Selected is react-aria's `data-selected`, not a prop. The selected pill takes
 * the Figma "Blue" variable (#0062E0) for its stroke and label, not the Accent.
 * The stroke sits inside the 32px pill, so it is an inset ring.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const pillTabVariants = cva([
	'inline-flex max-w-87.5 cursor-pointer items-center justify-center rounded-atlas-full bg-atlas-light px-3 py-1.5 text-center font-atlas-sans text-atlas-tab text-atlas-light-dark data-disabled:cursor-default data-selected:inset-ring data-selected:inset-ring-atlas-blue data-selected:text-atlas-blue',
	focusRing,
])

export interface PillTabsProps extends Omit<AriaToggleButtonGroupProps, 'className' | 'selectionMode'> {
	className?: string
}

/**
 * A row of pill tabs where exactly one is selected (a period switcher such as
 * "Q1 2026 / Q3 2026"), built on react-aria's ToggleButtonGroup in single
 * selection, so it is a radio group: arrow keys move between pills. The board
 * draws the pills alone, so the 8px gap between them is not drawn in the Figma.
 */
export function PillTabs({ className, disallowEmptySelection = true, ...props }: PillTabsProps) {
	return (
		<AriaToggleButtonGroup
			{...props}
			selectionMode="single"
			disallowEmptySelection={disallowEmptySelection}
			className={cn('flex flex-wrap items-center gap-2 font-atlas-sans', className)}
		/>
	)
}

export interface PillTabProps extends Omit<AriaToggleButtonProps, 'className' | 'children'> {
	className?: string
	children: ReactNode
}

/** One pill of a `PillTabs` row; give it an `id` the group's selection refers to. A long label truncates. */
export function PillTab({ className, children, ...props }: PillTabProps) {
	return (
		<AriaToggleButton {...props} className={cn(pillTabVariants(), className)}>
			<span className="min-w-0 truncate">{children}</span>
		</AriaToggleButton>
	)
}
